import { createActionGroup, emptyProps, props } from '@ngrx/store'

import { CodeEditorSelection } from '@flowx/designer/modules/monaco-code-editor/types'
import {
  ExplainBusinessRuleResponse,
  GenerateBusinessRuleFromDocumentResponse,
  GenerateBusinessRuleResponse,
  GenerateExpressionResponse,
} from '@flowx/designer/modules/new-ai/agents/developer/api/developer.api'
import {
  DocumentContextReference,
  Message,
  SendFeedbackPayload,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { AiState } from '@flowx/designer/modules/new-ai/store/ai.state'

const errorProps = props<{ statusCode: number; errorMsg?: any }>

export const AiCodeEditorContextMenuActions = createActionGroup({
  source: 'AiCodeEditorContextMenu',
  events: {
    improveCodeOptionClick: props<{ selection: CodeEditorSelection }>(),
    addCommentOptionClick: props<{ selection: CodeEditorSelection }>(),
  },
})

export const AiDeveloperActions = createActionGroup({
  source: 'AiDeveloper',
  events: {
    // general
    generate: props<{ prompt: string }>(),
    generateBusinessRuleStream: props<{ prompt: string }>(),

    keepDraft: emptyProps(),
    discardDraft: emptyProps(),
    sendFeedback: props<{ payload: SendFeedbackPayload }>(),

    // business rule
    extractBusinessRuleFromDocument: props<{
      documentContextReference: DocumentContextReference
    }>(),
    extractBusinessRuleFromDocumentSuccess: props<{
      response: GenerateBusinessRuleFromDocumentResponse
    }>(),
    extractBusinessRuleFromDocumentFailure: props<{ error?: any }>(),
    generateBusinessRule: props<{ prompt?: string }>(),
    generateBusinessRuleSuccess: props<{ response: GenerateBusinessRuleResponse }>(),
    generateBusinessRuleFailure: errorProps(),

    keepBusinessRuleDraft: emptyProps(),
    keepBusinessRuleDraftSuccess: emptyProps(),
    discardBusinessRuleDraft: emptyProps(),

    // expression
    generateExpression: props<{ prompt?: string }>(),
    generateExpressionSuccess: props<{ response: GenerateExpressionResponse }>(),
    generateExpressionFailure: errorProps(),

    keepExpressionDraft: emptyProps(),
    keepExpressionDraftSuccess: emptyProps(),
    discardExpressionDraft: emptyProps(),

    // gateway condition
    generateGatewayCondition: props<{ prompt: string; nodeId: string }>(),
    generateGatewayConditionSuccess: props<{ response: any }>(),
    generateGatewayConditionFailure: errorProps(),

    keepGatewayConditionDraft: emptyProps(),
    keepGatewayConditionDraftSuccess: emptyProps(),
    discardGatewayConditionDraft: emptyProps(),

    // explain
    explainBusinessRule: emptyProps(),
    explainBusinessRuleSuccess: props<{ response: ExplainBusinessRuleResponse }>(),
    explainBusinessRuleFailure: errorProps(),

    // fix warnings
    fixBusinessRuleWarnings: props<{
      payload: {
        expression: string
        errorMessages: string[]
      }
    }>(),

    // fix error
    fixBusinessRuleError: props<{
      payload: {
        expression: string
        errorMessage: string
      }
    }>(),
    fixBusinessRuleWarningsSuccess: props<{ response: any }>(),
    fixBusinessRuleWarningsFailure: errorProps(),

    fixBusinessRuleErrorSuccess: props<{ response: any }>(),
    fixBusinessRuleErrorFailure: errorProps(),

    keepFixWarningsBusinessRuleDraft: emptyProps(),
    keepBusinessRuleDraftFailure: props<{ error?: any }>(),
    keepFixErrorBusinessRuleDraftSuccess: emptyProps(),
    keepFixWarningsBusinessRuleDraftSuccess: emptyProps(),
    keepFixWarningsBusinessRuleDraftFailure: props<{ error?: any }>(),

    keepExpressionDraftFailure: props<{ error?: any }>(),
    keepFixErrorBusinessRuleDraft: emptyProps(),
    discardFixWarningsBusinessRuleDraft: props<{
      requestId: string
      sessionId: string
    }>(),
    discardFixErrorBusinessRuleDraft: props<{
      requestId: string
      sessionId: string
    }>(),

    // improve code
    improveCode: props<{ selection: CodeEditorSelection }>(),
    improveCodeSuccess: props<{ response: any }>(),
    improveCodeFailure: errorProps(),

    keepImprovedCodeDraft: emptyProps(),
    keepImprovedCodeDraftSuccess: emptyProps(),
    discardImprovedCodeDraft: emptyProps(),

    // add comment
    addComment: props<{ selection: CodeEditorSelection }>(),
    addCommentSuccess: props<{ response: any }>(),
    addCommentFailure: errorProps(),

    keepAddCommentDraft: emptyProps(),
    keepAddCommentDraftSuccess: emptyProps(),
    discardAddCommentDraft: emptyProps(),

    // custom component
    generateCustomComponent: props<{ prompt: string }>(),
    getCustomComponentAnswerEventStream: props<{
      response: Partial<Message>
    }>(),
    setCustomComponentContext: props<{
      context: AiState['customComponentContext']
    }>(),
    keepCustomComponentDraft: emptyProps(),
    keepCustomComponentDraftSuccess: emptyProps(),
    discardCustomComponentDraft: emptyProps(),

    // reusable function
    setReusableFunctionContext: props<{ context: any }>(),
    generateReusableFunction: props<{ prompt: string }>(),
    keepReusableFunctionDraft: emptyProps(),
    keepReusableFunctionDraftSuccess: emptyProps(),
    discardReusableFunctionDraft: emptyProps(),

    getAnswerEventStream: props<{
      response: Partial<Message>
    }>(),
  },
})
